import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Pencil, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Shared address card. Shows the logged-in user's address (line, city, state, pincode)
 * and lets them edit it in place. Persists via PUT /me/profile.
 */
export default function AddressCard({ compact = false, title = "My Address" }) {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    address_line: "",
    address_city: "",
    address_state: "",
    address_pincode: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        address_line: user.address_line || "",
        address_city: user.address_city || "",
        address_state: user.address_state || "",
        address_pincode: user.address_pincode || "",
      });
    }
  }, [user]);

  const hasAny =
    form.address_line || form.address_city || form.address_state || form.address_pincode;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", form);
      setUser?.(data.user);
      setEditing(false);
      toast.success("Address updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save address");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm({
      address_line: user?.address_line || "",
      address_city: user?.address_city || "",
      address_state: user?.address_state || "",
      address_pincode: user?.address_pincode || "",
    });
    setEditing(false);
  };

  return (
    <Card data-testid="address-card" className={`p-5 border-slate-200 ${compact ? "" : "shadow-sm"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#B45309]" />
          <h3 className="text-sm font-semibold text-[#041E26]">{title}</h3>
        </div>
        {!editing ? (
          <button
            data-testid="address-edit-btn"
            onClick={() => setEditing(true)}
            className="text-xs font-medium text-[#B45309] hover:text-[#7A1E38] flex items-center gap-1"
          >
            <Pencil className="w-3 h-3" /> {hasAny ? "Edit" : "Add"}
          </button>
        ) : null}
      </div>

      {!editing ? (
        hasAny ? (
          <p data-testid="address-display" className="text-sm text-slate-600 leading-relaxed">
            {[form.address_line, form.address_city, form.address_state, form.address_pincode]
              .filter(Boolean)
              .join(", ")}
          </p>
        ) : (
          <p className="text-sm text-slate-400 italic">No address added yet. Click Add to set one.</p>
        )
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">Address Line</Label>
            <Input
              data-testid="address-line-input"
              value={form.address_line}
              onChange={(e) => setForm({ ...form, address_line: e.target.value })}
              placeholder="House / Flat, Street, Area"
              className="mt-1 h-10 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-500">City</Label>
              <Input
                data-testid="address-city-input"
                value={form.address_city}
                onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                placeholder="e.g. Mumbai"
                className="mt-1 h-10 rounded-lg"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-500">State</Label>
              <Input
                data-testid="address-state-input"
                value={form.address_state}
                onChange={(e) => setForm({ ...form, address_state: e.target.value })}
                placeholder="e.g. Maharashtra"
                className="mt-1 h-10 rounded-lg"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">Pincode</Label>
            <Input
              data-testid="address-pincode-input"
              value={form.address_pincode}
              onChange={(e) => setForm({ ...form, address_pincode: e.target.value })}
              placeholder="6-digit PIN"
              maxLength={6}
              className="mt-1 h-10 rounded-lg"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              data-testid="address-save-btn"
              onClick={save}
              disabled={saving}
              className="h-9 rounded-lg bg-[#041E26] hover:bg-[#0A2F3C] text-white text-xs gap-1.5"
            >
              <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              data-testid="address-cancel-btn"
              onClick={cancel}
              variant="outline"
              className="h-9 rounded-lg text-xs gap-1.5"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
